import * as React from 'react';
import { useCallback } from 'react';
import BulletedMsg from '../../layout/BulletedMsg';
import { faGlobeAfrica, faQuestionCircle, faTimesCircle, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { Str } from '../../localization/translationKeys';
import { css } from '@emotion/core';
import { COLORS } from '../../globalStyle/colors';
import { logOutAction } from '../../auth/login/api/logoutAction';
import { useHistory, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAppState } from '../../core/redux/hooks';
import { useWindowSize } from '../../core/hooks/useWindowSize';
import { isNilOrEmpty, strictEquals } from '../../language/module';
import { useCurrentUser } from '../../auth/hooks';
import { logInfoAction } from '../../traceLogging/api/logInfoAction';
import { ActionType } from '../../core/redux/actionType';
import { AppState } from '../../core/redux/appState';


const styles = {
  root:       css`
    padding: 20px;
    border-radius: 8px;
    background-color: ${COLORS.LIGHTER_GREY};
    
    & > * {
      display: block;
      margin-bottom: 10px;
    }
    
    & > *:last-child {
      margin-bottom: 0;
    }
  `,
  option:     css`
    font-size: 12px;
    color: ${COLORS.DARK_GREY} !important;
    
    &:hover { 
      cursor: pointer;
      color: ${COLORS.BLUE} !important;
    }
  `,
  optionIcon: css`
    font-size: 20px;
  `,
  silentLink: css`
    text-decoration: none;
  `,
};


type Props = {
  className?: string;
  loginUrl: string;
  accountUrl: string;
  languageUrl: string;
  customerServiceUrl: string;
};


/**
 * AppActions: a component that renders the list of actions in the side menu, including: logout, language, account...
 *
 * PureComponent: true
 * Redux Connected: false
 */
const AppActions: React.FC<Props> = props => {
  const history     = useHistory();
  const dispatch    = useDispatch();
  const logoutUrl   = useAppState(state => state.auth.logoutUrl) ?? '';
  const log         = useSelector<AppState, boolean>(state => !!state.featureFlags?.logLogoutUrl, strictEquals);
  const currentUser = useCurrentUser();

  const { isMobile } = useWindowSize();

  const languages = useAppState(state => state.projectLanguages?.[`${state.projectSummary?.projectId}`] ?? []);

  const logLogoutUrl = useCallback(
    (url) => dispatch(logInfoAction('LogoutUrl = ' + url, ActionType.LogOut)),
    [dispatch],
  );

  const logout = useCallback(
    () => {
      dispatch(logOutAction(isMobile));
      if (!isNilOrEmpty(logoutUrl)) {
        if (log) {
          logLogoutUrl(logoutUrl);
        }
        window.location.replace(logoutUrl)
      } else {
        if (log) {
          logLogoutUrl(isNilOrEmpty(currentUser?.logoutUrl) ? 'Not Set' : currentUser?.logoutUrl);
        }
        isNilOrEmpty(currentUser?.logoutUrl) 
          ? history.push(props.loginUrl) 
          : window.location.replace(currentUser?.logoutUrl || '');
      }
    },
    [dispatch, isMobile, history, props.loginUrl, logoutUrl, currentUser, log, logLogoutUrl],
  );

  return (
    <div css={styles.root} className={props.className}>
      <Link to={props.accountUrl} css={styles.silentLink}>
        <BulletedMsg
          icon={faUserCircle}
          msg={Str.Account}
          iconStyle={styles.optionIcon}
          css={styles.option}
        />
      </Link>
      {
        languages.length > 1 && (
          <Link to={props.languageUrl} css={styles.silentLink}>
            <BulletedMsg
              icon={faGlobeAfrica}
              msg={Str.Language}
              iconStyle={styles.optionIcon}
              css={styles.option}
            />
          </Link>
        )
      }
      <Link to={props.customerServiceUrl} css={styles.silentLink}>
        <BulletedMsg
          icon={faQuestionCircle}
          msg={Str.CustomerService}
          iconStyle={styles.optionIcon}
          css={styles.option}
        />
      </Link>
      <BulletedMsg
        icon={faTimesCircle}
        msg={Str.LogOut}
        role='button'
        onClick={logout}
        iconStyle={styles.optionIcon}
        css={styles.option}
      />
    </div>
  );
};

export default AppActions;
